import React from 'react';
import { ChatMessage as ChatMessageType, ConfigBlock } from '../types';
import ConfirmationCard from './ConfirmationCard';
import QuestionPrompt from './QuestionPrompt';
import TypingIndicator from './TypingIndicator';
import UserAvatar, { User } from 'common/components/UserAvatar/UserAvatar';
import BEM from 'common/bem';

const classes = BEM.with('ChatMessage');

/** Apply inline markdown (**bold**, *italic*, `code`) to a single pre-classified line. */
function renderInline(line: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
    if (match[2]) {
      parts.push(<strong key={`${keyPrefix}-b${match.index}`}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={`${keyPrefix}-i${match.index}`}>{match[3]}</em>);
    } else if (match[4]) {
      parts.push(
        <code key={`${keyPrefix}-c${match.index}`} className={classes('code')}>
          {match[4]}
        </code>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < line.length) parts.push(line.slice(lastIndex));
  return parts.length > 0 ? parts : [line];
}

function renderTextContent(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let keyIdx = 0;

  for (const para of text.split(/\n{2,}/)) {
    const lines = para
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) continue;

    const nodes: React.ReactNode[] = [];
    const paraIdx = keyIdx;
    lines.forEach((line, li) => {
      if (/^-{3,}$/.test(line)) return;
      if (li > 0) nodes.push(<br key={`br-${paraIdx}-${li}`} />);
      nodes.push(
        ...renderInline(line.replace(/^#{1,6}\s+/, '').replace(/^[-*]\s+/, ''), `${paraIdx}-${li}`)
      );
    });

    if (nodes.length > 0) {
      elements.push(
        <div key={`tb-${keyIdx++}`} className={classes('assistant-bubble')}>
          {nodes}
        </div>
      );
    }
  }

  return elements;
}

function renderConfigBlock(block: ConfigBlock, key: number): React.ReactNode {
  return (
    <div className={classes('config-card')}>
      {block.intro && (
        <div className={classes('config-card-intro')}>
          {renderInline(block.intro, `${key}-intro`)}
        </div>
      )}
      {block.rows.map((row, i) => {
        if ('section' in row) {
          return (
            <div key={`${key}-s-${i}`} className={classes('config-section-title')}>
              {row.section}
            </div>
          );
        }
        return (
          <div key={`${key}-r-${i}`} className={classes('config-row')}>
            <span className={classes('config-row-label')}>{row.label}</span>
            <span className={classes('config-row-value')}>{row.value}</span>
          </div>
        );
      })}
    </div>
  );
}

type ChatMessageProps = {
  message: ChatMessageType;
  currentUser?: User;
  onSelectOption: (value: string, label?: string) => void;
  isLastAssistantMessage: boolean;
  hasUserReplied: boolean;
};

export default function ChatMessage({
  message,
  currentUser,
  onSelectOption,
  isLastAssistantMessage,
  hasUserReplied,
}: ChatMessageProps) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className={classes()}>
        <div className={classes('user-row')}>
          <div className={classes('user-bubble')}>{message.content}</div>
          <UserAvatar className={classes('user-avatar')} user={currentUser} size={32} />
        </div>
      </div>
    );
  }

  // ── Progress block routing ─────────────────────────────────────────────
  // Derive tool-running state from the last progress segment in this message.
  // `started` means a tool call is in flight; `ended` (or absent) means idle.
  const lastProgressSeg = [...message.segments].reverse().find((s) => s.kind === 'progress');
  const progressMessage =
    lastProgressSeg?.kind === 'progress' && lastProgressSeg.data.status === 'started'
      ? lastProgressSeg.data.message ?? ''
      : null;

  // A message whose segments are exclusively progress blocks carries no
  // displayable content — render as a typing indicator while streaming,
  // or nothing once complete (the next message takes over).
  const hasOnlyProgressSegments =
    message.segments.length > 0 && message.segments.every((s) => s.kind === 'progress');

  // Empty streaming state (first token not yet received)
  if (message.isStreaming && message.segments.length === 0) {
    return <TypingIndicator />;
  }

  if (hasOnlyProgressSegments) {
    if (message.isStreaming && progressMessage !== null) {
      return <TypingIndicator message={progressMessage || undefined} />;
    }
    return null;
  }

  // ── Full assistant message ─────────────────────────────────────────────
  return (
    <div className={classes()}>
      <div className={classes('assistant-group')}>
        <div className={classes('assistant-header')}>
          <div className={classes('avatar')}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <span className={classes('sender')}>TigerConnect Assistant</span>
        </div>

        {message.segments.map((segment, i) => {
          if (segment.kind === 'progress') {
            // Progress blocks are control signals — never render as content.
            return null;
          }
          if (segment.kind === 'text') {
            const nodes = renderTextContent(segment.content);
            if (nodes.length === 0) return null;
            return <React.Fragment key={i}>{nodes}</React.Fragment>;
          }
          if (segment.kind === 'config') {
            return <React.Fragment key={i}>{renderConfigBlock(segment.data, i)}</React.Fragment>;
          }
          if (segment.kind === 'confirmation') {
            // Do not surface org token values — sensitive, not for display.
            if (/token/i.test(segment.data.field)) return null;
            return <ConfirmationCard key={i} data={segment.data} />;
          }
          if (segment.kind === 'question') {
            const answered = !isLastAssistantMessage || hasUserReplied;
            if (answered) return null;
            return (
              <QuestionPrompt
                key={i}
                data={segment.data}
                onSelect={onSelectOption}
                disabled={!!message.isStreaming}
              />
            );
          }
          return null;
        })}
        {message.error && <div className={classes('error')}>{message.error}</div>}
      </div>
    </div>
  );
}
